import React, { useEffect, useState, useCallback } from "react";
import MetricCards from '../components/admin/analytics/MetricCards';
import SalesChart from '../components/admin/analytics/SalesChart';
import TopProduct from '../components/admin/analytics/TopProduct';
import ProductSalesTable from '../components/admin/analytics/ProductSalesTable';
import BranchSelector from '../components/admin/analytics/BranchSelector';
import EmptyState from '../components/CommonComponents/EmptyState';
import { BarChart3 } from 'lucide-react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

const API_URL = `${process.env.REACT_APP_API_URL}/api/admin/orders/analytics`;

const AnalyticsSkeleton = () => (
    <div className="min-h-screen bg-gray-50 p-6 animate-pulse">
        <div className="mb-8 flex items-center justify-between">
            <div className="h-10 w-56 rounded-2xl bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200" />
            <div className="h-10 w-64 rounded-xl bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200" />
        </div>

        <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-5">
            {Array.from({ length: 5 }).map((_, idx) => (
                <div key={idx} className="rounded-xl bg-white p-4 shadow">
                    <div className="mx-auto h-7 w-24 rounded-full bg-slate-200" />
                    <div className="mx-auto mt-3 h-3 w-14 rounded-full bg-slate-100" />
                </div>
            ))}
        </div>

        <div className="mb-8 flex flex-col gap-6">
            <div className="rounded-xl bg-white p-6 shadow">
                <div className="mb-4 flex items-center justify-between">
                    <div className="h-5 w-36 rounded-full bg-slate-200" />
                    <div className="flex items-center gap-2">
                        <div className="h-9 w-24 rounded-lg bg-slate-100" />
                        <div className="h-9 w-20 rounded-lg bg-slate-100" />
                        <div className="h-9 w-9 rounded-lg bg-slate-100" />
                    </div>
                </div>
                <div className="h-80 rounded-2xl bg-gradient-to-br from-slate-100 via-white to-slate-100 p-4">
                    <div className="flex h-full items-end gap-3">
                        {[48, 72, 58, 86, 54, 68, 78, 60, 74, 52, 80, 64].map((height, idx) => (
                            <div key={idx} className="flex flex-1 items-end">
                                <div
                                    className="w-full rounded-t-2xl bg-gradient-to-t from-indigo-200 via-indigo-100 to-slate-100"
                                    style={{ height: `${height}%` }}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="rounded-xl bg-white p-6 shadow">
                <div className="mb-4 h-5 w-40 rounded-full bg-slate-200" />
                <div className="h-72 rounded-2xl bg-gradient-to-br from-slate-100 via-white to-slate-100 p-4">
                    <div className="flex h-full items-end gap-4">
                        {[72, 55, 88, 64, 49, 77, 58, 69].map((height, idx) => (
                            <div key={idx} className="flex flex-1 items-end">
                                <div
                                    className="w-full rounded-t-2xl bg-gradient-to-t from-sky-200 via-sky-100 to-slate-100"
                                    style={{ height: `${height}%` }}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="rounded-xl bg-white p-6 shadow">
                <div className="mb-4 h-5 w-44 rounded-full bg-slate-200" />
                <div className="space-y-3">
                    {Array.from({ length: 8 }).map((_, idx) => (
                        <div key={idx} className="grid grid-cols-3 gap-4 border-b pb-3">
                            <div className="h-3.5 rounded-full bg-slate-200" />
                            <div className="h-3.5 rounded-full bg-slate-100" />
                            <div className="h-3.5 rounded-full bg-slate-100" />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    </div>
);

const Analytics = () => {
    const [salesRange, setSalesRange] = useState('day'); // 'day', 'week', 'month'
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [branchId, setBranchId] = useState("");
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    // Fetch analytics data
    const fetchAnalytics = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("token") || sessionStorage.getItem("token");
            const customerId = sessionStorage.getItem("customer_id") || localStorage.getItem("customer_id");

            let url = API_URL;
            const params = [];
            if (customerId) params.push(`customer_id=${customerId}`);
            if (branchId) params.push(`branch_id=${branchId}`);
            if (params.length) url += `?${params.join("&")}`;

            const response = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            const data = await response.json();
            setAnalytics(data);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [branchId]);

    useEffect(() => {
        fetchAnalytics();
    }, [fetchAnalytics]);

    // Normalize backend UTC date string to local Date object
    const normalizeDate = (dateStr) => {
        if (!dateStr) return null;
        const cleaned = String(dateStr).trim();
        // Always treat as UTC and convert to local
        if (/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) {
            // Parse as UTC midnight, then shift to local
            const utc = new Date(`${cleaned}T00:00:00Z`);
            return new Date(utc.getTime() + utc.getTimezoneOffset() * 60000 * -1);
        }
        // If already ISO string, parse as UTC
        if (/^\d{4}-\d{2}-\d{2}T/.test(cleaned)) {
            const utc = new Date(cleaned);
            return new Date(utc.getTime() + utc.getTimezoneOffset() * 60000 * -1);
        }
        // Fallback: parse as local
        const parsed = new Date(cleaned);
        return isNaN(parsed) ? null : parsed;
    };

    const getStartOfWeek = (date) => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = day === 0 ? -6 : 1 - day; // Monday as first day
        d.setDate(d.getDate() + diff);
        d.setHours(0, 0, 0, 0);
        return d;
    };

    const getStartOfMonth = (date) => {
        const d = new Date(date.getFullYear(), date.getMonth(), 1);
        d.setHours(0, 0, 0, 0);
        return d;
    };

    const filterByRange = (data, start, end) => {
        if (!Array.isArray(data)) return [];
        return data.filter(d => {
            const dt = normalizeDate(d.date);
            return dt && dt >= start && dt <= end;
        });
    };

    const sortByDateAsc = (data) => {
        if (!Array.isArray(data)) return [];
        return [...data].sort((a, b) => {
            const da = normalizeDate(a.date);
            const db = normalizeDate(b.date);
            if (!da || !db) return 0;
            return da - db;
        });
    };

    // Prepare chart data for monthly sales
    const getMonthName = (monthNum) => {
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        return months[monthNum - 1] || monthNum;
    };
    const monthlySales = (analytics?.salesByMonth ?? []).map(m => ({
        label: `${getMonthName(m.month)} ${m.year}`,
        totalSales: parseFloat(m.totalSales),
    })).reverse(); // reverse for chronological order
    const monthlyData = monthlySales.map(m => m.totalSales);

    const yearOptions = Array.from(
        new Set((analytics?.salesByMonth ?? []).map(m => Number(m.year)))
    ).filter(Boolean).sort((a, b) => b - a);

    useEffect(() => {
        if (yearOptions.length === 0) return;
        if (!yearOptions.includes(Number(selectedYear))) {
            setSelectedYear(yearOptions[0]);
        }
    }, [yearOptions, selectedYear]);

    const monthlyLabels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const monthlyDataByYear = monthlyLabels.map((_, idx) => {
        const month = idx + 1;
        const record = (analytics?.salesByMonth ?? []).find(
            m => Number(m.year) === Number(selectedYear) && Number(m.month) === month
        );
        return record ? parseFloat(record.totalSales) : 0;
    });
    const getOverview = () => {
        if (!analytics?.salesByDay) return { total: 0, orders: 0, avg: 0 };
        // Match backend date string directly to today's local date in 'YYYY-MM-DD' format (same as reports filter)
        const localYMD = new Date().toLocaleDateString('en-CA'); // 'YYYY-MM-DD'
        const today = (analytics.salesByDay ?? []).find(d => (d.date || '').trim() === localYMD);
        const endOfToday = new Date();
        endOfToday.setHours(23, 59, 59, 999);
        const week = filterByRange(analytics.salesByDay, getStartOfWeek(new Date()), endOfToday);
        // Fix: use todayDateLocal for getStartOfMonth argument
        const month = filterByRange(analytics.salesByDay, getStartOfMonth(new Date()), endOfToday);
        const totalSales = (arr) => arr.reduce((sum, d) => sum + parseFloat(d.totalSales), 0);
        const totalOrders = (arr) => arr.reduce((sum, d) => sum + parseInt(d.ordersCount), 0);
        const monthOrders = totalOrders(month);
        return {
            today: today ? parseFloat(today.totalSales) : 0,
            week: totalSales(week),
            month: totalSales(month),
            orders: monthOrders,
            avg: monthOrders > 0 ? totalSales(month) / monthOrders : 0,
        };
    };

    const overview = getOverview();

    // Helper: get filtered sales data for chart
    const getSalesByDayFiltered = () => {
        if (!analytics?.salesByDay) return [];
        if (salesRange === 'week') {
            const endOfToday = new Date();
            endOfToday.setHours(23, 59, 59, 999);
            return sortByDateAsc(
                filterByRange(analytics.salesByDay, getStartOfWeek(new Date()), endOfToday)
            );
        }
        if (salesRange === 'month') {
            const endOfToday = new Date();
            endOfToday.setHours(23, 59, 59, 999);
            return sortByDateAsc(filterByRange(analytics.salesByDay, getStartOfMonth(endOfToday), endOfToday));
        }
        return sortByDateAsc(analytics.salesByDay);
    };

    const salesByDayFiltered = getSalesByDayFiltered();
    const salesByDayLabels = salesByDayFiltered.map(d => d.date);
    const salesByDayData = salesByDayFiltered.map(d => parseFloat(d.totalSales));
    // ...existing code...

    // Prepare chart data for sales by day

    // Prepare chart data for top products (limit to 10 for chart)
    const top10 = (analytics?.topProducts ?? []).slice(0, 10);
    const topProductsLabels = top10.map(p => p.product || p.product?.name || p.product_id);
    const topProductsData = top10.map(p => parseInt(p.totalQty));
    const topProductsChart = {
        labels: topProductsLabels,
        datasets: [
            {
                label: 'Top 10 Products',
                data: topProductsData,
                backgroundColor: 'rgba(54, 162, 235, 0.6)',
                borderColor: 'rgba(54, 162, 235, 1)',
                borderWidth: 1,
            },
        ],
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className="flex items-center justify-between mb-8">
                <h1 className="text-3xl font-bold text-gray-900">Sales Analytics</h1>
                <BranchSelector value={branchId} onChange={setBranchId} />
            </div>
            {error && (
                <EmptyState
                    icon={BarChart3}
                    title="Failed to Load Analytics"
                    message="Check your internet connection and try again."
                    onRetry={fetchAnalytics}
                    retryLabel="Retry"
                />
            )}
            {!error && loading && <AnalyticsSkeleton />}
            {!error && !loading && analytics && (
                <>
                    {/* Metrics Card Row */}
                    <MetricCards overview={overview} />

                    {/* Stacked Full-Width Graphs: Sales and Top Products */}
                    <div className="flex flex-col gap-6 mb-8">
                        <SalesChart
                            salesRange={salesRange}
                            onSalesRangeChange={setSalesRange}
                            monthlyData={monthlyData}
                            monthlyLabels={monthlyLabels}
                            monthlyDataByYear={monthlyDataByYear}
                            salesByDayLabels={salesByDayLabels}
                            salesByDayData={salesByDayData}
                            analytics={analytics}
                            selectedYear={selectedYear}
                            onYearChange={setSelectedYear}
                            yearOptions={yearOptions}
                        />
                            <TopProduct topProductsChart={topProductsChart} />
                            <ProductSalesTable products={analytics?.topProducts ?? []} />
                    </div>
                </>
            )}
        </div>
    );
};

export default Analytics;
